import { FC, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailFields,
  MiPlaceholderList,
  MiSectionHeader,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import {
  filterMaterialsByAttributes,
  filterMaterialsByCategory,
  filterMaterialsBySearch,
  handleInputRestrictions,
} from "@miplatform/utils";
import { mdiTrashCanOutline } from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { CategoryFilterDrawer } from "../../../../components/FilterDrawer/CategoryFilterDrawer";
import { MaterialCard } from "../../../../components/MaterialCard/MaterialCard";
import { IItemData } from "../../Items.types";
import { useItemLinkItemTabViewModel } from "./useItemLinkItemTabViewModel";

export const ItemLinkItemTab: FC<ItemLinkItemTabProps> = ({
  form,
  extraTabProps = {},
}: ItemLinkItemTabProps) => {
  const { control, setValue, watch } = form;
  const { isReadOnly, setSelectedTab = () => null } = extraTabProps;
  const { classes, theme } = useStyles();
  const { category: { categoryId } = {}, linkedMaterials = [] } = watch();

  const {
    fetchMaterials,
    materials,
    fetchCategoryFilters,
    categoryFilterOptions,
    search,
    selectedCategory,
    handleCategoryClick,
    handleAttributeValueClick,
    selectedAttributeValues,
    handleReset,
    isLoading,
    setFilteredMaterials,
    filteredMaterials,
    handleResetResultContainer,
    debouncedSearch,
    openMaterialResults,
    hasFilters,
    handleSearch,
    handleRemoveItem,
    handleAddItem,
  } = useItemLinkItemTabViewModel({ watch, setValue });

  useEffect(() => {
    fetchCategoryFilters();
    fetchMaterials();
  }, []);

  useEffect(() => {
    let filtered = [...materials];
    if (selectedCategory?.categoryId) {
      filtered = filterMaterialsByCategory(filtered, selectedCategory);
    }
    if (Object.keys(selectedAttributeValues).length > 0) {
      filtered = filterMaterialsByAttributes(filtered, selectedAttributeValues);
    }
    if (debouncedSearch) {
      filtered = filterMaterialsBySearch(filtered, debouncedSearch);
    }
    setFilteredMaterials(filtered);
  }, [selectedCategory, selectedAttributeValues, debouncedSearch]);

  const commonDisabled = !categoryId || isReadOnly;

  const qtyField = (index: number) => [
    {
      name: `linkedMaterials[${index}].quantity`,
      formControl: control,
      sx: { width: 55 },
      minWidth: 55,
      disabled: commonDisabled,
      type: "number",
      onKeyDown: handleInputRestrictions,
      onInput: handleInputRestrictions,
    } as any,
  ];

  return (
    <Box className={classes.root}>
      <Box className={classes.headerContainer}>
        <Box className={classes.scrollableAccordionContent}>
          <ConfigAccordionHeader title="Linked Items">
            <MiSectionHeader
              subtitle="Add any related Materials that are required to complete the items installation. Select a Category on the right to begin filtering."
              variant="text"
              headerStyle={{ padding: 0 }}
            />
            {linkedMaterials.map((material, index) => {
              return (
                <MaterialCard
                  key={material.id}
                  item={material}
                  menuData={
                    !isReadOnly
                      ? [
                          {
                            label: "Remove Item",
                            icon: mdiTrashCanOutline,
                            color: theme.palette.red.main,
                            action: () => handleRemoveItem(material),
                          },
                        ]
                      : []
                  }
                  showPrice
                  leftAction={<MiDetailFields detailFields={qtyField(index)} />}
                />
              );
            })}
            {linkedMaterials.length === 0 && (
              <Box className={classes.placeholderListBox}>
                <MiPlaceholderList text="No linked items." />
              </Box>
            )}
          </ConfigAccordionHeader>
        </Box>
        <Box className={classes.buttonContainer}>
          <MiButton
            title="Previous"
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={() => setSelectedTab("cost pricing")}
          />
          <MiButton
            title="Next"
            variant={BUTTON_VARIANTS.CONTAINED}
            onClick={() => setSelectedTab("preview")}
          />
        </Box>
      </Box>
      <Box className={classes.drawerSection}>
        {openMaterialResults && (
          <Box className={classes.nestedDrawer}>
            <SearchResultsContainer
              open={openMaterialResults}
              onClose={handleResetResultContainer}
              isLoading={isLoading}
              alertTitle="Select Item(s) to link."
              alertDescription="Click Link Item to link this item."
              storageKey="link-item-search-results-alert"
              results={filteredMaterials}
              hasFilters={hasFilters}
              handleResetFilterSelection={handleReset}
              renderResults={() =>
                filteredMaterials.map((material) => {
                  const isSelected = linkedMaterials.some(
                    (linked) => linked.materialId === material.id
                  );
                  return (
                    <MaterialCard
                      key={material.id}
                      item={material}
                      isSelected={isSelected}
                      rightAction={
                        isSelected ? null : (
                          <MiButton
                            variant={BUTTON_VARIANTS.CONTAINED}
                            title="Link Item"
                            color="primary"
                            onClick={() => handleAddItem(material as any)}
                            disabled={commonDisabled}
                          />
                        )
                      }
                    />
                  );
                })
              }
            />
          </Box>
        )}
        {!isReadOnly && (
          <CategoryFilterDrawer
            title="Filter Items"
            categoryFilterOptions={categoryFilterOptions}
            search={search}
            selectedCategory={selectedCategory}
            selectedAttributeValues={selectedAttributeValues}
            setSearch={handleSearch}
            handleCategoryClick={handleCategoryClick}
            handleAttributeValueClick={handleAttributeValueClick}
            handleReset={handleReset}
            handleResetSelection={handleReset}
          />
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "hidden",
    height: "100%",
    width: "100%",
    position: "relative",
  },
  headerContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    margin: theme.spacing(2, 2, 0, 0),
    height: "100%",
    overflow: "hidden",
  },
  scrollableAccordionContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  drawerSection: {
    position: "relative",
    display: "flex",
    height: "100%",
  },
  nestedDrawer: {
    position: "absolute",
    right: "100%",
    top: 0,
    bottom: 0,
    width: 450,
    minWidth: 450,
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  placeholderListBox: {
    marginTop: theme.spacing(4),
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    justifyContent: "flex-end",
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadows[1],
    marginBottom: 4,
    marginTop: 4,
    position: "sticky",
    bottom: 0,
  },
}));

interface ItemLinkItemTabProps {
  form: UseFormReturn<IItemData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    setSelectedTab?: (tab: string) => void;
  };
}
